import React, { useMemo } from 'react';
import { CardTemplate } from '../../shared/CardTemplate';
import { FormattedMessage } from 'react-intl';
import {
  Column,
  Row,
  useTelemetry,
  Block,
  Color,
  Icon,
  LoadingIndicator,
  Text,
} from '@snowflake/core-ui';
import { useSignupFormController } from './SignupFormControllerContext';
import { SignupCardButton } from './SignupCardButton';
import { SignupEventName } from '../../../../utils/SignupLogger';
import { useSignupFormData } from './SignupFormDataContext';

interface CortexWaitlistStatusProps {
  onTryAgain: () => void;
}

export const CortexWaitlistStatus = ({ onTryAgain }: CortexWaitlistStatusProps) => {
  const { waitlistEntryFinished, waitlistErrorCode } = useSignupFormController();
  const { logAction } = useTelemetry();
  const [opacity, setOpacity] = React.useState(1);
  const [hasLoggedResult, setHasLoggedResult] = React.useState(false);

  // Track previous state to detect transition
  const prevFinishedRef = React.useRef(waitlistEntryFinished);

  // Trigger a smooth transition when the waitlist status changes
  React.useEffect(() => {
    if (!prevFinishedRef.current && waitlistEntryFinished) {
      setOpacity(0);
      setTimeout(() => setOpacity(1), 50);
    }
    prevFinishedRef.current = waitlistEntryFinished;
  }, [waitlistEntryFinished]);

  // Log when success/failure card is shown to user (UI telemetry)
  React.useEffect(() => {
    if (waitlistEntryFinished && !hasLoggedResult) {
      if (waitlistErrorCode) {
        // Log failure card shown
        logAction(
          SignupEventName.CORTEX_CODE_WAITLIST_FORM_ERROR_CARD_SHOWN,
          'ui_click',
          'CortexCodeWaitListCard',
          {
            errorCode: waitlistErrorCode,
            flowType: 'cortex_code_waitlist',
          },
        );
      } else {
        // Log success card shown
        logAction(
          SignupEventName.CORTEX_CODE_WAITLIST_FORM_SUCCESS_CARD_SHOWN,
          'ui_click',
          'CortexCodeWaitListCard',
          {
            flowType: 'cortex_code_waitlist',
          },
        );
      }
      setHasLoggedResult(true);
    }
  }, [waitlistEntryFinished, waitlistErrorCode, hasLoggedResult, logAction]);

  const renderTryAgainButton = () => {
    return (
      <SignupCardButton width="100%" onClick={onTryAgain}>
        <FormattedMessage id="Try again" />
      </SignupCardButton>
    );
  };

  return (
    <CardTemplate
      cardProps={{
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingBottom: '24px',
        fullbleed: true,
      }}
    >
      <div
        style={{
          opacity,
          transition: 'opacity 0.25s ease-in-out',
        }}
      >
        <Column rowGap="24px" width="100%">
          <Row align="center">
            <CortexWaitlistStatusContent />
          </Row>
          {waitlistEntryFinished && waitlistErrorCode && renderTryAgainButton()}
        </Column>
      </div>
    </CardTemplate>
  );
};

export const CortexWaitlistStatusContent = () => {
  const { email } = useSignupFormData();
  const { waitlistEntryFinished, waitlistErrorCode } = useSignupFormController();

  const icon = useMemo(() => {
    if (!waitlistEntryFinished) {
      return <LoadingIndicator />;
    }

    if (waitlistErrorCode) {
      return (
        <Block
          height="40px"
          width="40px"
          borderRadius="50%"
          overflow="hidden"
          backgroundColor={Color.Red50}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon icon="x" size={32} color={Color.White} backgroundColor="transparent" />
        </Block>
      );
    }

    return (
      <Block borderRadius="50%" overflow="hidden">
        <Icon
          icon="checkmark"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          size={40 as any /* 40 is from design but is not a standard size */}
          color={Color.White}
          backgroundColor={Color.Green70}
        />
      </Block>
    );
  }, [waitlistEntryFinished, waitlistErrorCode]);

  const title = useMemo(() => {
    if (!waitlistEntryFinished) {
      return 'Verifying your email...';
    }

    if (!waitlistErrorCode) {
      return "You're on the list!";
    }

    return <FormattedMessage id="Something went wrong" />;
  }, [waitlistEntryFinished, waitlistErrorCode]);

  const subtitle = useMemo(() => {
    if (!waitlistEntryFinished) {
      return null;
    }

    if (waitlistErrorCode) {
      return (
        <Column rowGap="16px">
          <Text>
            <FormattedMessage
              id="We weren't able to verify the email address you entered: {email}"
              values={{
                email: <Text weight="semi-bold">{email}</Text>,
              }}
            />
          </Text>
          <FormattedMessage id="Double check your email address and try again." />
        </Column>
      );
    }

    return (
      <Text>
        We will notify you as soon as Cortex Code CLI is ready for early access. Keep an eye on your
        inbox.
      </Text>
    );
  }, [waitlistEntryFinished, email, waitlistErrorCode]);

  return (
    <>
      {/* paddingtop of CardTemplate can't be configured. so we offset it with -8px below to match the design */}
      <Block textAlign="center" marginTop="-8px">
        <Block width="fit-content" margin="auto">
          {icon}
        </Block>
        <Block marginTop="16px" fontSize="20px" fontWeight={600}>
          {title}
        </Block>
        <Block fontSize="14px" marginTop="12px" lineHeight="150%">
          {subtitle}
        </Block>
      </Block>
    </>
  );
};
